import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { HStack, VStack } from "../components/utils";
import {
  ButtonSize,
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../pages/quickRound/CTAButtonComponents";
import { Label } from "../components/shared/InputField";
import { useUpdatePhysicalShareRanges } from "../queries/shareholders";
import SplitShareRangesAgTable from "../pages/shareholders/SplitShareRangesAgTable";

type UploadPhysicalShareRangesProps = {
  certificateId: string;
  existingRanges: number[][];
  existingPhysicalRanges?: number[][];
  onPrimaryAction: () => void;
  onSecondaryAction: () => void;
};

export function UploadPhysicalShareRanges({
  certificateId,
  existingRanges,
  existingPhysicalRanges = [],
  onPrimaryAction = () => {},
  onSecondaryAction = () => {},
}: UploadPhysicalShareRangesProps) {
  const { mutate: updatePhysicalShareRanges, isLoading } =
    useUpdatePhysicalShareRanges();

  const gridApi = useRef<any>(null);

  const [ranges] = useState<number[][]>(existingPhysicalRanges);

  const handleSubmit = () => {
    const currentData: number[][] = [];
    gridApi.current.api.forEachNode((node: any) => {
      if (node.data.from > 0 && node.data.to > 0) {
        currentData.push([node.data.from, node.data.to]);
      }
    });

    if (currentData.length === 0) {
      toast("Please add at least one valid share range", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    // Validate ranges
    for (const range of currentData) {
      if (range[0] > range[1]) {
        toast("Invalid range: 'From' value cannot be greater than 'To' value", {
          type: "error",
          autoClose: 2000,
        });
        return;
      }
    }

    updatePhysicalShareRanges(
      {
        certificateId,
        physicalRanges: currentData,
      },
      {
        onSuccess: () => {
          toast("Physical Share Ranges Updated Successfully", {
            type: "success",
            autoClose: 2000,
          });
          onPrimaryAction();
        },
        onError: (err: any) => {
          toast(err.response?.data?.errorMessage || "Failed to update ranges", {
            type: "error",
            autoClose: 2000,
          });
        },
      }
    );
  };

  return (
    <div className="w-full p-4">
      <HStack className="justify-between border-b pb-3">
        <p className="font-semibold text-lg ml-4 mr-5">
          Upload Physical Share Ranges
        </p>
        <XMarkIcon
          className="cursor-pointer h-5 font-medium"
          onClick={() => onSecondaryAction()}
        />
      </HStack>
      <VStack className="p-5">
        <HStack className="m-2 gap-5 flex-wrap">
          <Label className="text-sm font-normal items-center pr-10 m-2">
            Original Share Numbers :
          </Label>
          <div
            className={`grid ${
              existingRanges.length > 1 ? "grid-cols-3 gap-2" : ""
            }`}
          >
            {existingRanges
              .map((range) => `${range[0]}-${range[1]}`)
              .map((range, index) => (
                <p
                  key={index}
                  className={`text-xs font-semibold text-center cursor-pointer border rounded-lg border-black px-1.5 mt-1 ${
                    existingRanges.length === 1 ? "text-center" : ""
                  }`}
                >
                  {range}
                </p>
              ))}
          </div>
        </HStack>

        {existingPhysicalRanges.length > 0 && (
          <HStack className="m-2 gap-5 flex-wrap">
            <Label className="text-sm font-normal items-center pr-10 m-2">
              Current Physical Ranges :
            </Label>
            <div
              className={`grid ${
                existingPhysicalRanges.length > 1 ? "grid-cols-3 gap-2" : ""
              }`}
            >
              {existingPhysicalRanges
                .map((range) => `${range[0]}-${range[1]}`)
                .map((range, index) => (
                  <p
                    key={index}
                    className="text-xs font-semibold text-center cursor-pointer border rounded-lg border-green-600 text-green-600 px-1.5 mt-1"
                  >
                    {range}
                  </p>
                ))}
            </div>
          </HStack>
        )}

        <div className="m-2 mt-4">
          <Label className="text-sm font-medium mb-2">
            Enter New Physical Share Ranges
          </Label>
          <SplitShareRangesAgTable ranges={ranges} gridApi={gridApi} />
        </div>

        <HStack className="justify-between mt-10">
          <SecondaryCTAButton
            event-name="Cancel Upload Physical Share Ranges"
            onClick={() => {
              onSecondaryAction();
            }}
          >
            Cancel
          </SecondaryCTAButton>
          <PrimaryCTAButton
            event-name="Submit Upload Physical Share Ranges"
            buttonSize={ButtonSize.SMALL}
            onClick={handleSubmit}
            loading={isLoading}
          >
            Submit
          </PrimaryCTAButton>
        </HStack>
      </VStack>
    </div>
  );
}
